import React from "react";
import { useNavigate } from "react-router-dom";
import { Cpu, Wifi, WifiOff, AlertOctagon, Activity } from "lucide-react";
import { useDevices } from "@/hooks/useDevices";
import { useSummary } from "@/hooks/useSummary";
import {
  PageHeader, StatCard, DataTable, EmptyState, DeviceStatusBadge, type Column,
} from "@/components/ui";
import type { DeviceSummary } from "@/api/devices";

export function DeviceFleet() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDevices();
  const { data: summaryData } = useSummary();

  const devices = data?.devices ?? [];
  const s = summaryData?.summary;

  const columns: Column<DeviceSummary>[] = [
    {
      key: "name", header: "Device", sortable: true,
      sortValue: (d) => d.label ?? d.serial_number,
      render: (d) => (
        <span className="font-medium text-text">{d.label ?? d.serial_number}</span>
      ),
    },
    { key: "serial_number", header: "Serial", sortable: true },
    { key: "model", header: "Model", sortable: true },
    {
      key: "version", header: "Firmware",
      render: (d) => d.registered_version ?? "—", numeric: true,
    },
    {
      key: "last_seen_at", header: "Last seen", sortable: true,
      sortValue: (d) => d.last_seen_at ?? "",
      render: (d) => (d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "Never"),
      numeric: true,
    },
    {
      key: "status", header: "Status",
      render: (d) => <DeviceStatusBadge online={d.is_online} />,
    },
  ];

  return (
    <div className="space-y-lg">
      <PageHeader title="Devices" subtitle="Fleet overview" icon={Cpu} />

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-md">
          <StatCard label="Total" value={s.devices_total} icon={Cpu} tone="brand" />
          <StatCard label="Online" value={s.devices_online} icon={Wifi} tone="success" />
          <StatCard label="Offline" value={s.devices_offline} icon={WifiOff} />
          <StatCard
            label="Errors · 24h"
            value={s.errors_last_24h}
            icon={AlertOctagon}
            tone={s.errors_last_24h > 0 ? "danger" : "default"}
          />
          <StatCard label="Optics · 24h" value={s.optics_last_24h} icon={Activity} />
        </div>
      )}

      {error ? (
        <EmptyState title="Could not load devices" icon={Cpu} />
      ) : !isLoading && devices.length === 0 ? (
        <EmptyState title="No devices yet" hint="Devices appear here once registered." icon={Cpu} />
      ) : (
        <DataTable
          columns={columns}
          rows={devices}
          getRowKey={(d) => d.id}
          loading={isLoading}
          onRowClick={(d) => navigate(`/devices/${d.id}/overview`)}
        />
      )}
    </div>
  );
}

export default DeviceFleet;
